import { getAIModelById, getDefaultAIModel } from './aiModelService';
import { deductCredits } from './optimizedCreditService';
import { hasEnoughCreditsForAIModel } from './creditService';
import { db } from '../firebase';
import { doc, updateDoc, increment } from 'firebase/firestore';
import { callOpenAI, callGemini, callDeepSeek, callClaude } from './aiProviderService';
import { parseSizechartGenerationResponse } from './sizechartAIService';

/**
 * Generate a complete sizechart from scratch using AI with vision support
 * @param {string} userId - The user ID
 * @param {string} shopId - The shop ID
 * @param {string} productCategory - The product category
 * @param {string} targetCountry - The target country code
 * @param {string} responseLanguage - The language for the response
 * @param {string} inputText - Product information text
 * @param {string} modelId - The AI model ID to use
 * @param {boolean} includeBaseSizes - Whether to include base sizes
 * @param {string} garmentType - The garment type
 * @param {string} fitType - The fit type
 * @param {string} stretchLevel - The stretch level
 * @param {string} materialInfo - Material information
 * @param {Array} images - Array of image URLs for vision analysis
 * @returns {Promise<Object>} - The generated sizechart
 */
export const generateSizechartWithAIVision = async (
  userId, 
  shopId, 
  productCategory, 
  targetCountry, 
  responseLanguage, 
  inputText, 
  modelId = null, 
  includeBaseSizes = false, 
  garmentType = null, 
  fitType = null, 
  stretchLevel = null, 
  materialInfo = null, 
  images = []
) => {
  try {
    // Get the AI model to use
    let model = null;
    if (modelId) {
      model = await getAIModelById(modelId);
    }
    
    if (!model) {
      model = await getDefaultAIModel();
    }
    
    if (!model) {
      throw new Error('No AI model available for sizechart generation');
    }

    // Check if user has enough credits
    const creditCost = model.creditCostPerOutput || 1;
    const hasCredits = await hasEnoughCreditsForAIModel(userId, creditCost);
    
    if (!hasCredits) {
      throw new Error(`Not enough credits. You need ${creditCost} credits to generate this sizechart.`);
    }

    // Create the prompt
    let promptInstructions = `You are a professional clothing size analyst specializing in international size standards. I need you to create a complete sizechart for a ${productCategory} in ${targetCountry} sizing.`;
    
    // Add garment-specific information if available
    if (garmentType) {
      promptInstructions += `\n\nThis is a ${garmentType} type garment.`;
    }
    
    // Always include material info if available for AI to analyze
    if (materialInfo) {
      promptInstructions += `\n\nMaterial composition: ${materialInfo}`;
    }
    
    // Instructions for AI to analyze and determine fit type and stretch level
    promptInstructions += `\n\nBased on the product details and material composition, please determine:
1. The fit type (slim, regular, or loose)
2. The stretch level (none, slight, moderate, or high)

Include this information in your response as structured data with 'fit_type' and 'stretch_level' fields.

Instead of providing fit information for each size, provide a single general fit description for the entire garment. Only include size-specific notes if there's something unusual or special about a particular size that deviates from the general fit (e.g., 'Size XL runs slightly larger than expected').`;
    
    // Add instructions for extraction
    if (includeBaseSizes) {
      promptInstructions += `\n\nIMPORTANT: ONLY use the sizes and measurements provided in the input. DO NOT add extra sizes that weren't in the input.

### Extraction Rules:
1. **Identify the size system**: Look for any size indicators (S/M/L, numbers, ranges, etc.) in the input
2. **Extract measurements**: Find all measurement names and values (Length, Bust, Waist, Shoulder, etc.)
3. **Create consistent structure**: Always output with "Size" column for the main size, "${targetCountry} Size" for country-specific equivalent, and separate columns for each measurement
4. **Preserve all data**: Don't skip or modify any measurements from the input
5. **Match row count**: Output exactly as many rows as there are size entries in the input

For each size in the fit_info section:
1. Analyze how it compares to standard ${targetCountry} sizing
2. Provide a **unique** fit description (runs large, runs small, true to size) - use the provided fit type (${fitType || 'not specified'}) as a guide
3. Include specific buyer hints about measurements that might differ from expectations
4. Add body measurement recommendations for each key measurement (bust, waist, hip)
5. Assign an appropriate fit tag (${fitType ? fitType.charAt(0).toUpperCase() + fitType.slice(1) + ' Fit' : 'Regular Fit'})
6. Include specific measurement instructions to help customers measure correctly
7. **CRITICAL**: Each size MUST have a different, unique fit description - do NOT repeat the same description for all sizes

This detailed fit information will help customers choose the right size and reduce returns.`;
    } else {
      promptInstructions += `\n\nIMPORTANT: ONLY use the sizes and measurements provided in the input. DO NOT add extra sizes that weren't in the input.

### Extraction Rules:
1. **Identify the size system**: Look for any size indicators in the input and use the most appropriate representation for ${targetCountry}
2. **Extract measurements**: Find all measurement names and values from the input
3. **Create consistent structure**: Always output with "Size" column and separate columns for each measurement found
4. **Preserve all data**: Don't skip or modify any measurements from the input
5. **Match row count**: Output exactly as many rows as there are size entries in the input

For each size in the fit_info section:
1. Analyze how it compares to standard ${targetCountry} sizing
2. Provide a **unique** fit description (runs large, runs small, true to size) - use the provided fit type (${fitType || 'not specified'}) as a guide
3. Include specific buyer hints about measurements that might differ from expectations
4. Add body measurement recommendations for each key measurement (bust, waist, hip)
5. Assign an appropriate fit tag (${fitType ? fitType.charAt(0).toUpperCase() + fitType.slice(1) + ' Fit' : 'Regular Fit'})
6. Include specific measurement instructions to help customers measure correctly
7. **CRITICAL**: Each size MUST have a different, unique fit description - do NOT repeat the same description for all sizes

This detailed fit information will help customers choose the right size and reduce returns.`;
    }

    // Create the full prompt
    const prompt = `${promptInstructions}

### Unit Conversion and Requirements:
- **IMPORTANT**: Create columns for ONLY ONE unit system based on the target country (${targetCountry})
- Countries using inches: UK, US, CA, AU
- Countries using centimeters: EU, FR, DE, IT, ES, NL, CN, JP, KR
- If the source shows both units, extract values using the appropriate unit for ${targetCountry}
- If conversion is needed: CM to INCHES (divide by 2.54), INCHES to CM (multiply by 2.54)
- Preserve range values (e.g., "34-36") without converting to single values
- Do NOT create duplicate columns for the same measurement in different units

### JSON Format:
{
  "name": "${targetCountry} ${productCategory} Size Chart",
  "category": "${productCategory}",
  "countries": ["${targetCountry}"],
  "fit_type": "regular",
  "stretch_level": "moderate",
  "columns": [
    ${includeBaseSizes ? 
      `{ "key": "original_size", "name": "Size", "unit": "none" },
    { "key": "country_size", "name": "${targetCountry} Size", "unit": "none" },` : 
      `{ "key": "size", "name": "Size", "unit": "none" },`
    }
    // Add measurement columns with proper format:
    // { "key": "measurement_name", "name": "Display Name (unit)", "unit": "in" or "cm" }
  ],
  "rows": [
    // Extract actual sizes and measurements from the input
    ${includeBaseSizes ? 
      `{ "original_size": "Size from input", "country_size": "${targetCountry} size equivalent", "values": ["measurement1", "measurement2"] }` : 
      `{ "size": "Size from input", "values": ["measurement1", "measurement2"] }`
    }
  ],
  "fit_info": [
    {
      ${includeBaseSizes ? 
        `"original_size": "S",` : 
        `"size": "S",`
      }
      "fit_description": "Snug fit around the bust and waist, accentuating the A-line silhouette with slight ease due to the polyester blend.",
      "buyer_hint": "Ideal for those preferring a more fitted look without sacrificing comfort, benefiting from the dress's slight stretch.",
      "fit_tag": "Slim Fit"
    },
    {
      ${includeBaseSizes ? 
        `"original_size": "M",` : 
        `"size": "M",`
      }
      "fit_description": "Balanced fit with moderate ease through the bust and waist, enhancing comfort while maintaining the dress's feminine shape.",
      "buyer_hint": "Recommended for wearers seeking a comfortable regular fit that adapts well to body movement without being too tight.",
      "fit_tag": "Regular Fit"
    },
    {
      ${includeBaseSizes ? 
        `"original_size": "L",` : 
        `"size": "L",`
      }
      "fit_description": "More room across the bust and waist areas, creating a relaxed regular fit that complements the midi length and A-line cut.",
      "buyer_hint": "Suitable for those desiring extra ease without losing the dress's structured silhouette and feminine details.",
      "fit_tag": "Relaxed Regular Fit"
    }
    // Add more sizes as found in input - each with UNIQUE descriptions
  ]
}

Input data to analyze:
${inputText}

Your response must be valid JSON with no comments.`;

    // Prepare messages for vision-capable AI providers
    const messages = [
      {
        role: 'system',
        content: 'You are a professional clothing size analyst that provides structured JSON responses.'
      },
      {
        role: 'user',
        content: prompt
      }
    ];
    
    // Prepare request data with images if provided
    const requestData = {
      messages,
      temperature: model.temperature || 0.2,
      max_tokens: model.maxTokens || 2000,
      images: images || [],
      modelType: (images && images.length > 0) ? 'vision' : 'text'
    };
    
    // Ensure model has the right structure for aiProviderService
    if (!model.internalName && model.modelId) {
      model.internalName = model.modelId;
    }
    
    console.log('===== SIZE CHART GENERATION WITH VISION =====');
    console.log('Model:', model.displayName);
    console.log('Model ID:', model.modelId);
    console.log('Internal Name:', model.internalName);
    console.log('Provider:', model.provider);
    console.log('Images:', images.length > 0 ? `${images.length} image(s)` : 'No images');
    console.log('Prompt:', prompt.substring(0, 200) + '...');
    console.log('===============================================');
    
    // Call the appropriate AI provider based on the model
    let result;
    switch (model.provider) {
      case 'openai':
        const openaiResponse = await callOpenAI(model, requestData, userId);
        result = openaiResponse.choices[0].message.content;
        break;
      case 'deepseek':
        const deepseekResponse = await callDeepSeek(model, requestData, userId);
        result = deepseekResponse.choices[0].message.content;
        break;
      case 'gemini':
        const geminiResponse = await callGemini(model, requestData, userId);
        result = geminiResponse.candidates[0].content.parts[0].text;
        break;
      case 'claude':
        const claudeResponse = await callClaude(model, requestData, userId);
        result = claudeResponse.choices[0].message.content;
        break;
      default:
        throw new Error(`Unsupported AI provider: ${model.provider}`);
    }
    
    console.log('===== SIZE CHART GENERATION RESPONSE =====');
    console.log(result);
    console.log('========================================');
    
    // Parse the result (reuse the existing parser)
    const parsedResult = parseSizechartGenerationResponse(result, targetCountry);
    
    // Deduct credits for the operation
    await deductCredits(userId, creditCost, {
      shopId,
      type: 'sizechart_generation_vision',
      modelId: model.id,
      modelName: model.displayName,
      details: `Generated sizechart with vision for ${productCategory} (${targetCountry})`
    });
    
    // Update shop metrics
    if (shopId) {
      try {
        const shopRef = doc(db, 'shops', shopId);
        await updateDoc(shopRef, {
          'metrics.sizechartsGenerated': increment(1)
        });
        console.log(`Updated size charts generated count for shop ${shopId}`);
      } catch (error) {
        console.error('Error updating size charts generated count:', error);
      }
    }
    
    return parsedResult;
  } catch (error) {
    console.error('Error generating sizechart with AI vision:', error);
    throw error;
  }
};
